import { nip04Service } from './nip04Service';

const DEFAULT_BLOSSOM_SERVER = 'https://blossom.nostr.build';
const FALLBACK_SERVERS = [
    'https://blossom.pic.fi',
    'https://files.v0l.io',
    'https://blossom.hazrd.rocks',
    'https://nostr.it',
    'https://satellite.earth'
];

/**
 * Blossom Service - Decentralized media storage for Nostr
 * Based on the Blossom protocol (https://github.com/hzrd149/blossom)
 */
export class BlossomService {
    /**
     * Compute SHA-256 hash of a file
     */
    private async getFileHash(file: File): Promise<string> {
        const buffer = await file.arrayBuffer();
        const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }

    /**
     * Create an Auth header (Kind 24242) for upload
     */
    private async createAuthToken(
        file: File,
        method: 'extension' | 'seed',
        sk?: Uint8Array
    ): Promise<string> {
        const hash = await this.getFileHash(file);
        const eventTemplate = {
            kind: 24242,
            content: `Upload ${file.name}`,
            created_at: Math.floor(Date.now() / 1000),
            tags: [
                ['t', 'upload'],
                ['x', hash],
                ['size', file.size.toString()],
            ],
        };

        const signedEvent = await nip04Service.signEvent(eventTemplate, method, sk);
        return btoa(JSON.stringify(signedEvent));
    }

    /**
     * Upload file to Blossom server with fallback logic
     */
    async uploadFile(
        file: File,
        method: 'extension' | 'seed' | 'read_only',
        sk?: Uint8Array,
        preferredServer?: string
    ): Promise<string> {
        if (method === 'read_only') throw new Error('Yalnızca okuma modunda dosya yüklenemez.');

        const servers = preferredServer
            ? [preferredServer, DEFAULT_BLOSSOM_SERVER, ...FALLBACK_SERVERS]
            : [DEFAULT_BLOSSOM_SERVER, ...FALLBACK_SERVERS];

        let lastError: any = null;

        for (const server of servers) {
            try {
                const hash = await this.getFileHash(file);
                const authToken = await this.createAuthToken(file, method as any, sk);

                console.log(`📤 [Blossom] Attempting upload to ${server}...`);

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

                try {
                    const response = await fetch(`${server}/${hash}`, {
                        method: 'PUT',
                        headers: {
                            'Authorization': `Nostr ${authToken}`,
                            'Content-Type': file.type,
                        },
                        body: file,
                        signal: controller.signal
                    });

                    clearTimeout(timeoutId);

                    if (response.ok) {
                        const data = await response.json();
                        console.log(`✅ [Blossom] Upload successful: ${server}`);
                        return data.url || `${server}/${hash}`;
                    } else {
                        const errorText = await response.text().catch(() => 'No error detail');
                        console.warn(`⚠️ [Blossom] ${server} returned ${response.status}: ${errorText}`);
                    }
                } catch (fetchError: any) {
                    clearTimeout(timeoutId);
                    throw fetchError;
                }
            } catch (error: any) {
                if (error.name === 'AbortError') {
                    console.warn(`⚠️ [Blossom] Upload to ${server} timed out.`);
                } else {
                    console.warn(`⚠️ [Blossom] Upload to ${server} failed:`, error.message);
                }
                lastError = error;
                // Continue to next server
            }
        }

        throw new Error(
            `Resim yüklenemedi. DNS veya bağlantı sorunu olabilir.\n` +
            `Denenen sunucu sayısı: ${servers.length}\n` +
            `Son hata: ${lastError?.message || 'Bağlantı zaman aşımı'}\n\n` +
            `İpucu: VPN kullanıyorsanız kapatmayı veya farklı bir ağ denemeyi tercih edebilirsiniz.`
        );
    }

    /**
     * Helper to check if a URL is an image
     */
    isImageUrl(url: string): boolean {
        return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(url) || url.includes('blossom');
    }
}

export const blossomService = new BlossomService();
